import Link from 'next/link';
import React from 'react';
import { Image as MediaType } from 'types';

import clsxm from '@/lib/clsxm';
import { makeTitle } from '@/lib/makeTitle';
import { isVideoUrl } from '@/utils/mediaType';
import { getOptimizedImageUrl } from '@/utils/useOptimizedImage';

/**
 * One tile in a public gallery (style pages, "More images like this", the
 * video galleries).
 *
 * The galleries are fed by `similarity_search_images_v3`, which returns stills
 * and clips mixed together and drops the `is_video` column, so the media kind
 * is detected from the URL. Clips render as a muted <video> that plays on
 * hover and links to the video detail page instead of the image one — before
 * this they were rendered through <img> and showed up as broken tiles.
 */
const GalleryMediaTile = ({
  media,
  className,
  /** Fixed-height grids (UserImagesStatic) crop; masonry grids don't. */
  objectFit = 'auto',
  /** Static showcases render the tile without a link. */
  linkless = false,
  /** Static showcases keep the caption bar visible instead of on hover. */
  alwaysShowTitle = false,
}: {
  media: MediaType;
  className?: string;
  objectFit?: 'auto' | 'cover';
  linkless?: boolean;
  alwaysShowTitle?: boolean;
}) => {
  const isVideo = isVideoUrl(media.image_url);
  const title = makeTitle(String(media.title)).substring(0, 100);
  // There is no poster/thumbnail column on `images`, and `preload='metadata'`
  // alone leaves the tile blank until playback starts. The `#t=0.1` media
  // fragment makes the browser seek to (and paint) an early frame, which acts
  // as the poster. isVideoUrl() strips the fragment, so detection is unaffected.
  const videoSrc = `${media.image_url}#t=0.1`;
  const mediaClassName = clsxm(
    'rounded-md',
    objectFit === 'cover' ? 'h-full w-full object-cover' : 'h-full w-full',
    className,
  );

  const content = (
    <>
      <div className='relative'>
        {isVideo ? (
          <video
            src={videoSrc}
            title={media.title}
            aria-label={media.title}
            className={mediaClassName}
            preload='metadata'
            muted
            loop
            playsInline
            onMouseEnter={(e) => {
              const target = e.currentTarget;
              target.play().catch(() => {
                // Autoplay can be refused (e.g. reduced-motion / power saving)
                // — the poster frame is still a valid tile.
              });
            }}
            onMouseLeave={(e) => {
              const target = e.currentTarget;
              target.pause();
              target.currentTime = 0;
            }}
          />
        ) : (
          <img
            src={getOptimizedImageUrl(media.image_url, 'thumb')}
            alt={media.title}
            title={media.title}
            loading='lazy'
            className={mediaClassName}
          />
        )}
        {isVideo && (
          <div className='pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-150 group-hover:opacity-0'>
            <div className='rounded-full bg-black/50 p-3'>
              <svg
                className='h-8 w-8 text-white'
                fill='currentColor'
                viewBox='0 0 20 20'
                aria-hidden='true'
              >
                <path d='M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z' />
              </svg>
            </div>
          </div>
        )}
      </div>
      <div
        className={clsxm(
          'absolute bottom-0 left-0 right-0',
          alwaysShowTitle
            ? 'bg-slate-900 bg-opacity-60 p-2'
            : 'bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 transition-opacity duration-150 group-hover:opacity-100',
        )}
      >
        <p className='text-sm text-white [text-shadow:_0_1px_0_rgb(0_0_0_/_40%)]'>
          {title}
        </p>
      </div>
    </>
  );

  if (linkless) return content;

  return (
    <Link
      href={
        isVideo
          ? `/ai-video-generator/videos/${media.slug}`
          : `/ai-image-generator/images/${media.slug}`
      }
      className='hover:cursor-pointer'
    >
      {content}
    </Link>
  );
};

export default GalleryMediaTile;
